import React, { useEffect, useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../AuthContext';
import { Edit, Trash, Upload, Table, Plus,AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import './AdminQuizzes.css';
import Navbar from "../Navbar/Navbar";

// Helper function to parse CSV lines with proper handling of quoted fields
const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i < line.length) {
        const char = line[i];
        const nextChar = line[i + 1];
        
        if (char === '"') {
            if (inQuotes && nextChar === '"') {
                // Escaped quote
                current += '"';
                i += 2;
            } else {
                // Toggle quote state
                inQuotes = !inQuotes;
                i++;
            }
        } else if (char === ',' && !inQuotes) {
            // Field separator
            result.push(current.trim());
            current = '';
            i++;
        } else {
            current += char;
            i++;
        }
    }
    
    // Add the last field
    result.push(current.trim());
    return result;
};

const AdminQuizzes = () => {
    const [quizzes, setQuizzes] = useState([]);
    const [localQuizzes, setLocalQuizzes] = useState([]); // Local copy for draft changes
    const [hasChanges, setHasChanges] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [originalQuiz, setOriginalQuiz] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadProgress, setUploadProgress] = useState('');
    const [previewData, setPreviewData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [uploadConfig, setUploadConfig] = useState({
        targetQuizId: 1,
        quizName: 'TrueIn Assessment',
        quizDescription: 'Assessment',
        quizPrice: 1999.00
    });
    const [showUploadConfig, setShowUploadConfig] = useState(false);
    const [newQuiz, setNewQuiz] = useState({
        question: '',
        category: '',
        sub_category: '',
        answers: [],
        quiz_id: null,
    });
    const { user } = useAuth();

    useEffect(() => {
        fetchQuizzes();
    }, [user]);

    const fetchQuizzes = async () => {
        try {
            const { data, error } = await supabase
                .from('quizzes')
                .select(`ques_id, question, category, sub_category, created_at, quiz_details (quiz_name), answers, quiz_id`)
                .order('ques_id', { ascending: true });

            if (error) throw error;
            setQuizzes(data);
            setLocalQuizzes(JSON.parse(JSON.stringify(data))); // Create a deep copy for local editing
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to assign category based on points
    const assignCategoryByPoints = (points) => {
        const pointsInt = parseInt(points);
        if (pointsInt === 1) return 'Ignorance';
        if (pointsInt === 2 || pointsInt === 3) return 'Passion';
        if (pointsInt === 4 || pointsInt === 5) return 'Goodness';
        return '';
    };

    // Helper function to return all SWOT fields as they are
    const getSWOTDisplay = (index, answer) => {
        // Return all SWOT fields as they are, no filtering based on index
        return { 
            weakness: answer.weakness || '',
            strength: answer.strength || '',
            scw: answer.scw || ''
        };
    };

    // Enhanced Excel/CSV upload handler - Preview mode
    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadProgress('Reading file...');
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            try {
                let jsonData = [];
                
                if (file.name.endsWith('.csv')) {
                    // Handle CSV files with proper parsing for quoted fields
                    const text = e.target.result;
                    const lines = text.split('\n');
                    
                    // Parse CSV header
                    const headers = parseCSVLine(lines[0]);
                    
                    for (let i = 1; i < lines.length; i++) {
                        if (lines[i].trim()) {
                            const values = parseCSVLine(lines[i]);
                            const row = {};
                            headers.forEach((header, index) => {
                                row[header] = values[index] || '';
                            });
                            jsonData.push(row);
                        }
                    }
                } else {
                    // Handle Excel files
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    jsonData = XLSX.utils.sheet_to_json(worksheet);
                }

                setUploadProgress('Processing data...');
                
                // Transform the data to match your database structure
                const transformedData = [];
                
                for (const row of jsonData) {
                    // Skip empty rows
                    if (!row.Question && !row.question) continue;
                    
                    // Extract basic question info - handle different column name formats
                    const quiz_id = parseInt(row.quiz_id) || parseInt(row['Sr. No.']) || 1;
                    const question = row.Question || row.question || '';
                    const category = row['Groupng Main Parameters'] || row.category || '';
                    const sub_category = row['Grouping Sub Parameter'] || row.sub_category || '';
                    const max_marks = 5; // Default max marks

                    let answers = [];

                    // Handle your CSV format with separate columns for options, scores, etc.
                    if (row['Option 1'] || row.option1) {
                        // Your CSV format with separate columns
                        for (let i = 1; i <= 5; i++) {
                            const option = row[`Option ${i}`] || row[`option${i}`] || '';
                            const points = parseInt(row[`Score Option ${i}`] || row[`points${i}`]) || 1;
                            const remarks = row[`Report Phrase-Option ${i}`] || row[`Report Phrase Option ${i}`] || row[`remarks${i}`] || '';
                            
                            // Get all SWOT fields for the current option
                            const weakness = row[`Weakness Option${i}`]?.trim() || '';
                            const scw = row[`Strength Cum Weakness Option ${i}`]?.trim() || '';
                            const strength = row[`Strength Option ${i}`]?.trim() || '';
                            
                            // Only add if option exists
                            if (option && option.trim()) {
                                answers.push({
                                    option: option.trim(),
                                    points,
                                    remarks: remarks.trim(),
                                    category: assignCategoryByPoints(points),
                                    weakness: weakness.trim(),
                                    strength: strength.trim(),
                                    scw: scw.trim()
                                });
                            }
                        }
                    } else if (row.answers) {
                        // Handle JSON format (from database export)
                        try {
                            const parsedAnswers = JSON.parse(row.answers);
                            answers = parsedAnswers.map((answer, index) => ({
                                option: answer.option || '',
                                points: parseInt(answer.points) || 1,
                                remarks: answer.remarks || '',
                                category: answer.category || assignCategoryByPoints(answer.points),
                                weakness: answer.weakness || '',
                                strength: answer.strength || '',
                                scw: answer.scw || ''
                            }));
                        } catch (parseError) {
                            console.error('Error parsing answers JSON:', parseError);
                        }
                    }

                    // Only add if we have valid data
                    if (question && answers.length > 0) {
                        transformedData.push({
                            quiz_id,
                            question,
                            category,
                            sub_category,
                            answers,
                            max_marks,
                            created_by: user?.id
                        });
                    }
                }

                setPreviewData(transformedData);
                setShowPreview(true);
                setShowUploadConfig(true); // Show config modal first
                setUploadProgress('File processed successfully! Configure quiz details and review the data below.');
                
            } catch (err) {
                setError(`File processing failed: ${err.message}`);
                setUploadProgress('');
            }
        };

        if (file.name.endsWith('.csv')) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    const handleBackupAndClear = async () => {
        try {
            setUploadProgress('Backing up existing data...');
            
            // First, clear the old_quizzes table
            const { error: clearError } = await supabase
                .from('old_quizzes')
                .delete()
                .neq('id', 0); // Delete all records
            
            if (clearError) throw clearError;
            
            // Get all existing quiz data
            const { data: existingQuizzes, error: fetchError } = await supabase
                .from('quizzes')
                .select('*');
            
            if (fetchError) throw fetchError;
            
            if (existingQuizzes && existingQuizzes.length > 0) {
                // Insert into old_quizzes table
                const { error: insertError } = await supabase
                    .from('old_quizzes')
                    .insert(existingQuizzes.map(quiz => ({
                        question: quiz.question,
                        category: quiz.category,
                        answers: quiz.answers,
                        created_by: quiz.created_by,
                        created_at: quiz.created_at,
                        sub_category: quiz.sub_category,
                        quiz_id: quiz.quiz_id,
                        original_ques_id: quiz.ques_id,
                        created_at: new Date().toISOString()  // Add current timestamp
                    })));
                
                if (insertError) throw insertError;
            }
            
            // Clear the quizzes table
            const { error: deleteError } = await supabase
                .from('quizzes')
                .delete()
                .neq('ques_id', 0);
            
            if (deleteError) throw deleteError;
            
            // Note: We'll handle sequential numbering manually in the insert
            
            setUploadProgress('Backup completed. Ready to upload new data...');
            return true;
        } catch (err) {
            setError(`Backup failed: ${err.message}`);
            setUploadProgress('');
            return false;
        }
    };
    
    // Submit preview data to database
    const handleSubmitPreview = async () => {
        try {
            // First backup existing data and clear the table
            const backupSuccess = await handleBackupAndClear();
            if (!backupSuccess) return;
            
            setUploadProgress('Processing upload data...');
            
            // Use configured quiz ID and details
            const { targetQuizId, quizName, quizDescription, quizPrice } = uploadConfig;
            
            // Check if the target quiz exists in quiz_details table
            const { data: existingQuiz, error: quizCheckError } = await supabase
                .from('quiz_details')
                .select('quiz_id, quiz_name')
                .eq('quiz_id', targetQuizId)
                .single();
            
            if (quizCheckError && quizCheckError.code === 'PGRST116') {
                // Quiz doesn't exist, create it
                setUploadProgress(`Creating quiz "${quizName}"...`);
                
                const { error: insertQuizError } = await supabase
                    .from('quiz_details')
                    .insert([{
                        quiz_id: targetQuizId,
                        quiz_name: quizName,
                        description: `${quizDescription} with ${previewData.length} questions`,
                        price: parseFloat(quizPrice),
                        is_active: true,
                        num_questions: previewData.length
                    }]);
                
                if (insertQuizError) throw insertQuizError;
            } else if (quizCheckError) {
                throw quizCheckError;
            } else {
                // Quiz exists, update the number of questions and optionally the name
                setUploadProgress(`Updating quiz "${existingQuiz.quiz_name}"...`);
                
                const { error: updateQuizError } = await supabase
                    .from('quiz_details')
                    .update({ 
                        num_questions: previewData.length,
                        description: `${quizDescription} with ${previewData.length} questions`,
                        quiz_name: quizName, // Update name if user changed it
                        price: parseFloat(quizPrice)
                    })
                    .eq('quiz_id', targetQuizId);
                
                if (updateQuizError) throw updateQuizError;
            }
            
            setUploadProgress('Uploading questions to database...');
            
            // Prepare data for database insertion - assign sequential ques_id starting from 1
            const dataToInsert = previewData.map((item, index) => ({
                ques_id: index + 1, // Sequential numbering starting from 1
                quiz_id: targetQuizId, // Assign all questions to the configured quiz
                question: item.question,
                category: item.category,
                sub_category: item.sub_category,
                answers: item.answers,
                created_by: user?.id
            }));

            const { data, error } = await supabase
                .from('quizzes')
                .insert(dataToInsert);

            if (error) throw error;
            
            setUploadProgress(`Upload completed successfully! ${previewData.length} questions added to Quiz ID ${targetQuizId}.`);
            setTimeout(() => {
                setUploadProgress('');
                setShowPreview(false);
                setPreviewData([]);
                setShowUploadConfig(false);
                // Refresh the page to ensure clean state
                window.location.reload();
            }, 3000);
            
        } catch (err) {
            setError(`Upload failed: ${err.message}`);
            setUploadProgress('');
        }
    };

    // Cancel preview
    const handleCancelPreview = () => {
        setShowPreview(false);
        setPreviewData([]);
        setUploadProgress('');
        setShowUploadConfig(false);
        window.location.reload();
    };

    const handleAddQuestion = (e) => {
        e.preventDefault();
        try {
            const maxQuesId = localQuizzes.length > 0 
                ? Math.max(...localQuizzes.map(q => q.ques_id)) 
                : 0;
            const newQuesId = maxQuesId + 1;

            // Process answers with category assignment
            const processedAnswers = newQuiz.answers.map((answer, index) => ({
                ...answer,
                category: assignCategoryByPoints(answer.points),
                ...getSWOTDisplay(index, answer)
            }));

            const newQuestion = {
                ...newQuiz,
                ques_id: newQuesId,
                answers: processedAnswers,
                created_at: new Date().toISOString(),
                created_by: user?.id,
                quiz_details: null // This will be populated on submit
            };

            // Update local state
            setLocalQuizzes([...localQuizzes, newQuestion]);
            setHasChanges(true);
            
            // Reset form
            setAddModalOpen(false);
            setNewQuiz({
                question: '',
                category: '',
                sub_category: '',
                answers: [],
                quiz_id: null,
            });
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAddAnswer = () => {
        setNewQuiz({
            ...newQuiz,
            answers: [
                ...newQuiz.answers,
                { 
                    option: '', 
                    points: 1, 
                    remarks: '', 
                    category: '',
                    weakness: '',
                    strength: '',
                    scw: ''
                },
            ],
        });
    };

    const handleAnswerChange = (index, field, value) => {
        const updatedAnswers = [...newQuiz.answers];
        updatedAnswers[index][field] = value;
        
        // Auto-assign category when points change
        if (field === 'points') {
            updatedAnswers[index].category = assignCategoryByPoints(value);
        }
        
        setNewQuiz({ ...newQuiz, answers: updatedAnswers });
    };

    const [newAnswer, setNewAnswer] = useState({
        option: '',
        points: 1,
        remarks: '',
        category: '',
        weakness: '',
        strength: '',
        scw: ''
    });

    const handleUpdateQuestion = (e) => {
        e.preventDefault();
        try {
            // Process answers with category assignment
            const updatedQuiz = {
                ...selectedQuiz,
                answers: selectedQuiz.answers.map((answer, index) => ({
                    ...answer,
                    category: assignCategoryByPoints(answer.points),
                    ...getSWOTDisplay(index, answer)
                }))
            };

            // Update local state
            const updatedQuizzes = localQuizzes.map(q => 
                q.ques_id === updatedQuiz.ques_id ? updatedQuiz : q
            );
            
            setLocalQuizzes(updatedQuizzes);
            setHasChanges(true);
            setEditModalOpen(false);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleAnswerEdit = (index, field, value) => {
        const updatedAnswers = [...selectedQuiz.answers];
        updatedAnswers[index][field] = value;
        
        // Auto-assign category when points change
        if (field === 'points') {
            updatedAnswers[index].category = assignCategoryByPoints(value);
        }
        
        setSelectedQuiz({ ...selectedQuiz, answers: updatedAnswers });
    };

    const handleAddAnswerToExisting = () => {
        const answerWithCategory = {
            ...newAnswer,
            category: assignCategoryByPoints(newAnswer.points)
        };
        
        setSelectedQuiz({
            ...selectedQuiz,
            answers: [...selectedQuiz.answers, answerWithCategory]
        });
        setNewAnswer({ 
            option: '', 
            points: 1, 
            remarks: '', 
            category: '',
            weakness: '',
            strength: '',
            scw: ''
        });
    };

    const handleRemoveAnswer = (index) => {
        const updatedAnswers = selectedQuiz.answers.filter((_, i) => i !== index);
        setSelectedQuiz({ ...selectedQuiz, answers: updatedAnswers });
    };

    const handleEditClick = (quiz) => {
        const quizCopy = JSON.parse(JSON.stringify(quiz));
        setSelectedQuiz(quizCopy);
        setOriginalQuiz(JSON.parse(JSON.stringify(quiz))); // Store the original state
        setEditModalOpen(true);
    };

    const handleCancelEdit = () => {
        // Reset to original state when canceling
        if (originalQuiz) {
            setSelectedQuiz(JSON.parse(JSON.stringify(originalQuiz)));
        }
        setEditModalOpen(false);
        setOriginalQuiz(null);
    };

    // State to track deleted question IDs
    const [deletedQuestionIds, setDeletedQuestionIds] = useState([]);

    // Submit all changes to the database using the same logic as CSV upload
    const submitChanges = async () => {
        try {
            // First backup existing data and clear the table
            const backupSuccess = await handleBackupAndClear();
            if (!backupSuccess) {
                throw new Error('Failed to backup existing data');
            }
            
            // Sort questions to maintain order
            const sortedQuizzes = [...localQuizzes].sort((a, b) => a.ques_id - b.ques_id);
            
            // Prepare data for insertion with sequential ques_id starting from 1
            const dataToInsert = sortedQuizzes.map((quiz, index) => {
                const { ques_id, quiz_details, ...quizData } = quiz;
                return {
                    ...quizData,
                    ques_id: index + 1, // Force sequential IDs starting from 1
                    quiz_id: quizData.quiz_id || 1, // Default to 1 if not set
                    created_at: quizData.created_at || new Date().toISOString(),
                    created_by: user?.id,
                    // Update answer references to maintain order
                    answers: quizData.answers.map((answer, ansIndex) => ({
                        ...answer,
                        question_id: index + 1 // Update answer's question reference
                    }))
                };
            });

            // Insert all questions in a single batch
            const { error } = await supabase
                .from('quizzes')
                .insert(dataToInsert);

            if (error) throw error;
            
            // Reset states
            setHasChanges(false);
            setDeletedQuestionIds([]);
            await fetchQuizzes(); // Refresh the data
            
            alert(`${dataToInsert.length} questions saved successfully!`);
            
            // Refresh the page to ensure clean state
            window.location.reload();
        } catch (err) {
            setError(`Failed to save changes: ${err.message}`);
        }
    };

    const filteredQuizzes = localQuizzes.filter((quiz, index) => {
        const sno = (index + 1).toString();
        const searchLower = searchTerm.toLowerCase();
        return (
            sno.includes(searchLower) ||
            quiz.category.toLowerCase().includes(searchLower) ||
            quiz.question.toLowerCase().includes(searchLower) ||
            (quiz.sub_category && quiz.sub_category.toLowerCase().includes(searchLower))
        );
    });

    const handleDeleteQuestion = async (ques_id) => {
        try {
            // Track the deletion first
            setDeletedQuestionIds(prev => [...new Set([...prev, ques_id])]);
            
            // Then update the local quizzes to remove the question from the UI
            setLocalQuizzes(prev => {
                const updated = prev.filter(q => q.ques_id !== ques_id);
                return updated;
            });
            
            // Also update the original quizzes to prevent issues with the delete tracking
            setQuizzes(prev => prev.filter(q => q.ques_id !== ques_id));
            
            setHasChanges(true);
            
            // Show success message without interrupting the flow
            console.log(`Question ${ques_id} marked for deletion. Click "Submit Changes" to confirm.`);
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <div className="loading-container">Loading...</div>;
    if (error) return <div className="error-message">Error: {error}</div>;

    return (
        <div>
            <Navbar />
            <div className="admin-quizzes-container">
                {/* Unsaved Changes Banner */}
                {hasChanges && (
                    <div className="unsaved-changes-banner">
                        <div className="banner-content">
                            <span className="banner-message">
                                <AlertCircle size={20} className="banner-icon" />
                                You have unsaved changes
                            </span>
                            <button 
                                className="submit-changes-btn"
                                onClick={submitChanges}
                            >
                                Submit Changes
                            </button>
                        </div>
                    </div>
                )}

                <div className="header-section">
                    <div className="header-row">
                        <h1><Table size={28} /> Manage Assessment</h1>
                    </div>
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by SNo, category, sub-category, or question..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="action-buttons">
                        <label className="excel-upload">
                            <Upload size={20} />
                            <input 
                                type="file" 
                                accept=".xlsx,.xls,.csv" 
                                onChange={handleExcelUpload} 
                            />
                        </label>
                        <button className="add-quiz" onClick={() => setAddModalOpen(true)}>
                            <Plus size={20} /> Add New
                        </button>
                    </div>
                </div>

                {uploadProgress && (
                    <div className="upload-progress">
                        {uploadProgress}
                    </div>
                )}

                {showPreview && (
                    <div className="preview-section">
                        <div className="preview-header">
                            <h2>Preview Upload Data ({previewData.length} questions)</h2>
                            <div className="quiz-config-summary">
                                <span>Target Quiz ID: <strong>{uploadConfig.targetQuizId}</strong></span>
                                <span>Quiz Name: <strong>{uploadConfig.quizName}</strong></span>
                                <span>Price: <strong>₹{uploadConfig.quizPrice}</strong></span>
                                <button 
                                    className="config-btn" 
                                    onClick={() => setShowUploadConfig(true)}
                                >
                                    Configure Quiz Details
                                </button>
                            </div>
                            <div className="preview-actions">
                                <button 
                                    className="submit-preview-btn" 
                                    onClick={handleSubmitPreview}
                                    disabled={previewData.length === 0}
                                >
                                    Submit to Database
                                </button>
                                <button 
                                    className="cancel-preview-btn" 
                                    onClick={handleCancelPreview}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        <div className="preview-table-container">
                            <table className="preview-table">
                                <thead>
                                    <tr>
                                        <th>Quiz ID</th>
                                        <th>Question</th>
                                        <th>Category</th>
                                        <th>Sub Category</th>
                                        <th>Answers</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{uploadConfig.targetQuizId}</td>
                                            <td>{item.question}</td>
                                            <td>{item.category}</td>
                                            <td>{item.sub_category}</td>
                                            <td>
                                                <div className="preview-answers">
                                                    {item.answers.map((answer, idx) => (
                                                        <div key={idx} className="preview-answer-item">
                                                            <strong>Option {idx + 1}:</strong> {answer.option} 
                                                            <span className="answer-details">
                                                                (Points: {answer.points}, Category: {answer.category})
                                                            </span>
                                                            {answer.remarks && <div><em>Remarks: {answer.remarks}</em></div>}
                                                            {answer.weakness && <div><em>Weakness: {answer.weakness}</em></div>}
                                                            {answer.strength && <div><em>Strength: {answer.strength}</em></div>}
                                                            {answer.scw && <div><em>SCW: {answer.scw}</em></div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <table className="quiz-table">
                    <thead>
                        <tr>
                            <th>SNo</th>
                            <th>Quiz ID</th>
                            <th>Question</th>
                            <th>Category</th>
                            <th>Sub Category</th>
                            <th>Quiz Name</th>
                            <th>Created At</th>
                            <th>Answers</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredQuizzes.map((quiz, index) => (
                            <tr key={quiz.ques_id}>
                                <td>{index + 1}</td>
                                <td>{quiz.quiz_id}</td>
                                <td>{quiz.question}</td>
                                <td>{quiz.category}</td>
                                <td>{quiz.sub_category}</td>
                                <td>{quiz.quiz_details?.quiz_name}</td>
                                <td>{new Date(quiz.created_at).toLocaleDateString()}</td>
                                <td>
                                    <div className="answers-container">
                                        {quiz.answers.map((answer, idx) => {
                                            const swotDisplay = getSWOTDisplay(idx, answer);
                                            return (
                                                <div key={idx} className="answer-item">
                                                    <strong>Option {idx + 1}:</strong> {answer.option}
                                                    <div><strong>Points:</strong> {answer.points}</div>
                                                    <div><strong>Category:</strong> {answer.category}</div>
                                                    <div><strong>Remarks:</strong> {answer.remarks}</div>
                                                    {swotDisplay.weakness && <div><strong>Weakness:</strong> {swotDisplay.weakness}</div>}
                                                    {swotDisplay.strength && <div><strong>Strength:</strong> {swotDisplay.strength}</div>}
                                                    {swotDisplay.scw && <div><strong>SCW:</strong> {swotDisplay.scw}</div>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </td>
                                <td className="actions">
                                    <button
                                        className="edit-btn"
                                        onClick={() => handleEditClick(quiz)}
                                    >
                                        <Edit size={18} />
                                    </button>
                                    <button className="delete-btn" onClick={() => handleDeleteQuestion(quiz.ques_id)}>
                                        <Trash size={18} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {editModalOpen && (
                    <div className="modal-overlay">
                        <div className="edit-modal">
                            <h2>Edit Question</h2>
                            <form onSubmit={handleUpdateQuestion}>
                                <div className="form-group">
                                    <label>Question</label>
                                    <textarea
                                        value={selectedQuiz.question}
                                        onChange={(e) => setSelectedQuiz({ ...selectedQuiz, question: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        value={selectedQuiz.category}
                                        onChange={(e) => setSelectedQuiz({ ...selectedQuiz, category: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sub Category</label>
                                    <input
                                        type="text"
                                        value={selectedQuiz.sub_category || ''}
                                        onChange={(e) => setSelectedQuiz({ ...selectedQuiz, sub_category: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Answers</label>
                                    {selectedQuiz.answers.map((answer, index) => {
                                        const swotDisplay = getSWOTDisplay(index, answer);
                                        return (
                                            <div key={index} className="answer-form-extended">
                                                <input
                                                    type="text"
                                                    placeholder="Option"
                                                    value={answer.option}
                                                    onChange={(e) => handleAnswerEdit(index, 'option', e.target.value)}
                                                />
                                                <input
                                                    type="number"
                                                    placeholder="Points"
                                                    value={answer.points}
                                                    onChange={(e) => handleAnswerEdit(index, 'points', e.target.value)}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Category (Auto-assigned)"
                                                    value={assignCategoryByPoints(answer.points)}
                                                    readOnly
                                                    className="readonly-field"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Remarks"
                                                    value={answer.remarks}
                                                    onChange={(e) => handleAnswerEdit(index, 'remarks', e.target.value)}
                                                />
                                                <div className="swot-fields">
                                                    <input
                                                        type="text"
                                                        placeholder="Weakness"
                                                        value={answer.weakness || ''}
                                                        onChange={(e) => handleAnswerEdit(index, 'weakness', e.target.value)}
                                                        title="Weakness for this option"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="SCW"
                                                        value={answer.scw || ''}
                                                        onChange={(e) => handleAnswerEdit(index, 'scw', e.target.value)}
                                                        title="Strength cum Weakness for this option"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Strength"
                                                        value={answer.strength || ''}
                                                        onChange={(e) => handleAnswerEdit(index, 'strength', e.target.value)}
                                                        title="Strength for this option"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="remove-answer"
                                                    onClick={() => handleRemoveAnswer(index)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        );
                                    })}
                                    <div className="new-answer-form-extended">
                                        <input
                                            type="text"
                                            placeholder="New Option"
                                            value={newAnswer.option}
                                            onChange={(e) => setNewAnswer({ ...newAnswer, option: e.target.value })}
                                        />
                                        <input
                                            type="number"
                                            placeholder="Points"
                                            value={newAnswer.points}
                                            onChange={(e) => setNewAnswer({ 
                                                ...newAnswer, 
                                                points: e.target.value,
                                                category: assignCategoryByPoints(e.target.value)
                                            })}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Category (Auto-assigned)"
                                            value={assignCategoryByPoints(newAnswer.points)}
                                            readOnly
                                            className="readonly-field"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Remarks"
                                            value={newAnswer.remarks}
                                            onChange={(e) => setNewAnswer({ ...newAnswer, remarks: e.target.value })}
                                        />
                                        <div className="swot-fields">
                                            <input
                                                type="text"
                                                placeholder="Weakness"
                                                value={newAnswer.weakness || ''}
                                                onChange={(e) => setNewAnswer({ ...newAnswer, weakness: e.target.value })}
                                                title="Weakness for this option"
                                            />
                                            <input
                                                type="text"
                                                placeholder="SCW"
                                                value={newAnswer.scw || ''}
                                                onChange={(e) => setNewAnswer({ ...newAnswer, scw: e.target.value })}
                                                title="Strength cum Weakness for this option"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Strength"
                                                value={newAnswer.strength || ''}
                                                onChange={(e) => setNewAnswer({ ...newAnswer, strength: e.target.value })}
                                                title="Strength for this option"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="add-answer"
                                            onClick={handleAddAnswerToExisting}
                                        >
                                            Add Answer
                                        </button>
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={handleCancelEdit}>
                                        Cancel
                                    </button>
                                    <button type="submit">Save Changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {addModalOpen && (
                    <div className="modal-overlay">
                        <div className="edit-modal">
                            <h2>Add New Question</h2>
                            <form onSubmit={handleAddQuestion}>
                                <div className="form-group">
                                    <label>Question</label>
                                    <textarea
                                        value={newQuiz.question}
                                        onChange={(e) => setNewQuiz({ ...newQuiz, question: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <input
                                        type="text"
                                        value={newQuiz.category}
                                        onChange={(e) => setNewQuiz({ ...newQuiz, category: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Sub Category</label>
                                    <input
                                        type="text"
                                        value={newQuiz.sub_category}
                                        onChange={(e) => setNewQuiz({ ...newQuiz, sub_category: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Quiz ID</label>
                                    <input
                                        type="number"
                                        value={newQuiz.quiz_id}
                                        onChange={(e) => setNewQuiz({ ...newQuiz, quiz_id: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Answers</label>
                                    {newQuiz.answers.map((answer, index) => (
                                        <div key={index} className="answer-form-extended">
                                            <input
                                                type="text"
                                                placeholder="Option"
                                                value={answer.option}
                                                onChange={(e) => handleAnswerChange(index, 'option', e.target.value)}
                                            />
                                            <input
                                                type="number"
                                                placeholder="Points"
                                                value={answer.points}
                                                onChange={(e) => handleAnswerChange(index, 'points', e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Category (Auto-assigned)"
                                                value={assignCategoryByPoints(answer.points)}
                                                readOnly
                                                className="readonly-field"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Remarks"
                                                value={answer.remarks}
                                                onChange={(e) => handleAnswerChange(index, 'remarks', e.target.value)}
                                            />
                                            <div className="swot-fields">
                                                <input
                                                    type="text"
                                                    placeholder="Weakness"
                                                    value={answer.weakness || ''}
                                                    onChange={(e) => handleAnswerChange(index, 'weakness', e.target.value)}
                                                    title="Weakness for this option"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="SCW"
                                                    value={answer.scw || ''}
                                                    onChange={(e) => handleAnswerChange(index, 'scw', e.target.value)}
                                                    title="Strength cum Weakness for this option"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Strength"
                                                    value={answer.strength || ''}
                                                    onChange={(e) => handleAnswerChange(index, 'strength', e.target.value)}
                                                    title="Strength for this option"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="remove-answer"
                                                onClick={() => handleRemoveAnswer(index)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button type="button" className="add-answer" onClick={handleAddAnswer}>
                                        Add Answer
                                    </button>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setAddModalOpen(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit">Add Question</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Quiz Configuration Modal */}
                {showUploadConfig && (
                    <div className="modal-overlay">
                        <div className="edit-modal">
                            <h2>Configure Quiz Details</h2>
                            <div className="form-group">
                                <label>Quiz ID</label>
                                <input
                                    type="number"
                                    value={uploadConfig.targetQuizId}
                                    onChange={(e) => setUploadConfig({
                                        ...uploadConfig,
                                        targetQuizId: parseInt(e.target.value) || 1
                                    })}
                                    min="1"
                                />
                                <small>Choose which quiz to assign all questions to</small>
                            </div>
                            <div className="form-group">
                                <label>Quiz Name</label>
                                <input
                                    type="text"
                                    value={uploadConfig.quizName}
                                    onChange={(e) => setUploadConfig({
                                        ...uploadConfig,
                                        quizName: e.target.value
                                    })}
                                    placeholder="Enter quiz name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Quiz Description</label>
                                <input
                                    type="text"
                                    value={uploadConfig.quizDescription}
                                    onChange={(e) => setUploadConfig({
                                        ...uploadConfig,
                                        quizDescription: e.target.value
                                    })}
                                    placeholder="Enter quiz description"
                                />
                            </div>
                            <div className="form-group">
                                <label>Quiz Price (₹)</label>
                                <input
                                    type="number"
                                    value={uploadConfig.quizPrice}
                                    onChange={(e) => setUploadConfig({
                                        ...uploadConfig,
                                        quizPrice: parseFloat(e.target.value) || 0
                                    })}
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <div className="upload-summary">
                                <p><strong>Summary:</strong></p>
                                <p>• {previewData.length} questions will be uploaded</p>
                                <p>• All questions will be assigned to Quiz ID: {uploadConfig.targetQuizId}</p>
                                <p>• Questions will be numbered 1 to {previewData.length}</p>
                            </div>
                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    onClick={() => setShowUploadConfig(false)}
                                >
                                    Continue to Preview
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => {
                                        setShowUploadConfig(false);
                                        handleCancelPreview();
                                    }}
                                >
                                    Cancel Upload
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminQuizzes;